<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import {
  useActivateInvite,
  useDeclineInvite,
  useValidateInvite,
  type ValidateInviteResponse,
} from "@/queries/orgs";
import { ApiError } from "@/helpers/api-error";
import BlockUI from "@/components/BlockUI.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import LoginCard from "@/views/login/LoginCard.vue";

type Step = "loading" | "details" | "password" | "success" | "declined" | "error";

const route = useRoute();
const token = computed(() => route.query.token as string | undefined);

const {
  inviteDetails,
  inviteDetailsError,
  refetchInviteDetails,
} = useValidateInvite(token);
const {
  activateInviteAsync,
  activateInvitePending,
  activateInviteReset,
} = useActivateInvite();
const {
  declineInviteAsync,
  declineInvitePending,
  declineInviteReset,
} = useDeclineInvite();

const step = ref<Step>("loading");
const errorMsg = ref("");
const invitation = ref<ValidateInviteResponse | null>(null);
const password = ref("");
const confirmPassword = ref("");
const passwordError = ref("");
const termsAccepted = ref(false);
const submitting = computed(() => activateInvitePending.value || declineInvitePending.value);
const initialLoadError = computed(() =>
  token.value ? inviteDetailsError.value : new Error("Invalid invitation link. No token was provided."));

watch(inviteDetails, (details) => {
  if (details && step.value === "loading") {
    invitation.value = details;
    step.value = "details";
  }
}, { immediate: true });

async function handleAccept() {
  if (!invitation.value) return;

  if (invitation.value.isNewUser && invitation.value.authType === "password") {
    step.value = "password";
    return;
  }

  await activate(null);
}

async function handlePasswordSubmit() {
  passwordError.value = "";

  if (!password.value) {
    passwordError.value = "Password is required.";
    return;
  }

  if (password.value.length < 8) {
    passwordError.value = "Password must be at least 8 characters.";
    return;
  }

  if (password.value !== confirmPassword.value) {
    passwordError.value = "Passwords do not match.";
    return;
  }

  await activate(password.value);
}

async function activate(pw: string | null) {
  if (!token.value) return;

  activateInviteReset();
  try {
    const result = await activateInviteAsync({
      token: token.value,
      password: pw ?? undefined,
    });
    step.value = "success";
    setTimeout(() => {
      window.location.href = result.redirectUrl || "/";
    }, 2000);
  } catch (error) {
    errorMsg.value = getErrorMessage(error, "Failed to activate invitation. Please try again.");
    step.value = "error";
  }
}

async function handleDecline() {
  if (!token.value) return;

  declineInviteReset();
  try {
    await declineInviteAsync({ token: token.value });
    step.value = "declined";
  } catch (error) {
    errorMsg.value = getErrorMessage(error, "Failed to decline invitation.");
    step.value = "error";
  }
}

function getErrorMessage(error: unknown, fallback: string) {
  if (error instanceof ApiError) return error.userMessage;
  return error instanceof Error ? error.message : fallback;
}
</script>

<template>
  <LoginCard title="Accept Invitation">
    <SkeletonLoader
      v-if="step === 'loading'"
      type="panel"
      :lines="5"
      :error="initialLoadError"
      @retry="refetchInviteDetails"
    />

    <BlockUI v-else :active="submitting">
      <div v-if="step === 'details' && invitation">
        <h5 class="fw-semibold mb-1">You've been invited</h5>
        <p class="text-muted small mb-4">Review the details below before accepting.</p>

        <div class="bg-body-tertiary rounded p-3 mb-4">
          <div class="mb-2">
            <span class="text-muted small">Organization</span>
            <div class="fw-semibold">{{ invitation.orgName }}</div>
          </div>
          <div class="mb-2">
            <span class="text-muted small">Your email</span>
            <div class="fw-semibold">{{ invitation.email }}</div>
          </div>
          <div>
            <span class="text-muted small">Role</span>
            <div class="fw-semibold">{{ invitation.role }}</div>
          </div>
        </div>

        <div class="form-check mb-4">
          <input
            id="terms"
            v-model="termsAccepted"
            class="form-check-input"
            type="checkbox"
            :disabled="submitting"
          />
          <label class="form-check-label small" for="terms">
            I agree to the terms of service and privacy policy
          </label>
        </div>

        <div class="d-grid gap-2">
          <button
            class="btn btn-primary"
            :disabled="!termsAccepted || submitting"
            @click="handleAccept"
          >
            Accept Invitation
          </button>
          <button
            class="btn btn-outline-secondary"
            :disabled="submitting"
            @click="handleDecline"
          >
            Decline
          </button>
        </div>
      </div>

      <div v-else-if="step === 'password'">
        <h5 class="fw-semibold mb-1">Create your password</h5>
        <p class="text-muted small mb-4">Set a password to complete your account setup.</p>

        <div v-if="passwordError" class="alert alert-danger small py-2">
          {{ passwordError }}
        </div>

        <div class="mb-3">
          <label class="form-label small fw-medium">Password</label>
          <input
            v-model="password"
            type="password"
            class="form-control"
            placeholder="At least 8 characters"
            autocomplete="new-password"
            :disabled="submitting"
            @keyup.enter="handlePasswordSubmit"
          />
        </div>

        <div class="mb-4">
          <label class="form-label small fw-medium">Confirm password</label>
          <input
            v-model="confirmPassword"
            type="password"
            class="form-control"
            placeholder="Re-enter password"
            autocomplete="new-password"
            :disabled="submitting"
            @keyup.enter="handlePasswordSubmit"
          />
        </div>

        <div class="d-grid gap-2">
          <button
            class="btn btn-primary"
            :disabled="submitting"
            @click="handlePasswordSubmit"
          >
            Create Account &amp; Join
          </button>
          <button
            class="btn btn-link text-muted small"
            :disabled="submitting"
            @click="step = 'details'"
          >
            Back
          </button>
        </div>
      </div>

      <div v-else-if="step === 'success'" class="text-center py-3">
        <i class="bi bi-check-circle-fill text-success invite-status-icon"></i>
        <h5 class="mt-3 fw-semibold">You're in!</h5>
        <p class="text-muted small">
          Welcome to <strong>{{ invitation?.orgName }}</strong>. Redirecting you now...
        </p>
      </div>

      <div v-else-if="step === 'declined'" class="text-center py-3">
        <i class="bi bi-x-circle text-secondary invite-status-icon"></i>
        <h5 class="mt-3 fw-semibold">Invitation declined</h5>
        <p class="text-muted small">You've declined the invitation. You can close this page.</p>
        <router-link to="/" class="btn btn-outline-secondary btn-sm mt-2">
          Go to home
        </router-link>
      </div>

      <div v-else-if="step === 'error'" class="text-center py-3">
        <i class="bi bi-exclamation-circle-fill text-danger invite-status-icon"></i>
        <h5 class="mt-3 fw-semibold">Something went wrong</h5>
        <p class="text-danger small">{{ errorMsg }}</p>
        <router-link to="/" class="btn btn-outline-secondary btn-sm mt-2">
          Go to home
        </router-link>
      </div>
    </BlockUI>
  </LoginCard>
</template>

<style lang="scss" scoped>
.invite-status-icon {
  font-size: 3rem;
}
</style>
